// The built-in maps, generated from recipes with a seeded random generator (the same every time).
//
// A stage is a few "modules" in a row (zigzags, walls with a window to hop through, moving blocks,
// pillars, curves, stairs, drops, shaped blocks, ramps, boosters, long jumps...), then a checkpoint.
// Every gap is a fraction of what a jump can reach at a "design speed": 250 u/s leaving a checkpoint
// (prespeed), growing with each hop as a strafing player picks up speed. Later maps use smaller
// blocks, bigger fractions, faster design speeds, more stages and harder modules.

import { DIST_EPSILON } from '../../sim/constants';
import { columns, floorZ, pools, scatter, T, towers, trees, type Decor } from './scenery';
import { PROP_SIZES } from './propSizes';
import { NEW_RECIPES, OLD_DECOR } from './builtinThemes';
import type { Shape } from './shapes';
import {
  UPDOWN_TRAVEL,
  type BoxKind,
  type LevelBox,
  type LevelDef,
  type LevelProp,
  type LevelTheme,
  type MaterialName,
  type Vec3Tuple,
} from './types';

const GRAVITY = 800;
const JUMP = 301.993377;
/** Player box half width: you can take off 16 units past an edge and land 16 before one. */
const HALF = 16;

/** Time in the air for a jump that lands `dh` higher (negative: lower). */
export function airTime(vz: number, dh: number): number {
  const disc = vz * vz - 2 * GRAVITY * dh;
  if (disc < 0) return 0;
  return (vz + Math.sqrt(disc)) / GRAVITY;
}

/** Furthest edge-to-edge gap a jump at horizontal speed `v` clears. */
export function maxGap(v: number, dh: number, vz = JUMP): number {
  return v * airTime(vz, dh) + 2 * HALF;
}

export type Module =
  | 'line'
  | 'zigzag'
  | 'window'
  | 'curve'
  | 'stairs'
  | 'drop'
  | 'pillars'
  | 'shapes'
  | 'movers'
  | 'elevator'
  | 'boost'
  | 'ramp'
  | 'longjump'
  // Pieces from real bhop maps (only in the newer maps and the builder).
  | 'pads'
  | 'windgap'
  | 'updraft'
  | 'lowgrav'
  | 'surf'
  | 'ladder'
  | 'swim'
  | 'spinners'
  | 'blinkers';

/** How the course turns at its checkpoints (default 'snake': 90 degrees every other one). */
export type Layout = 'snake' | 'arc' | 'zig';

export type Look = [MaterialName, string];

export interface Recipe {
  name: string;
  title: string;
  difficulty: number;
  stages: number;
  seed: number;
  theme: LevelTheme;
  start: Look;
  checkpoint: Look;
  end: Look;
  /** Block looks, cycled per stage. */
  blocks: Look[];
  /** Walls, ramps and other solid pieces. */
  solid: Look;
  /** Block size at the first and the last stage. */
  size: [number, number];
  /** Fraction of the reachable gap used, first and last stage. */
  reach: [number, number];
  /** Design speed the stage builds up to, first and last stage. */
  topSpeed: [number, number];
  /** Modules this map uses (the first ones show up early, later ones later). */
  modules: Module[];
  scenery: (s: Scenery) => void;
  /** Scenery models (render only), placed with a random stream of their own. */
  decorate?: (d: Decor) => void;
  layout?: Layout;
  /** Space pieces by their real distance along the line of travel (the newer maps; see Builder). */
  exact?: boolean;
}

export interface Scenery {
  path: Vec3Tuple[];
  platforms: Vec3Tuple[];
  add(min: Vec3Tuple, max: Vec3Tuple, material: MaterialName, color: string, shape?: Shape): void;
  rand(): number;
  bounds: { min: Vec3Tuple; max: Vec3Tuple };
  clearOf(x: number, y: number, clear: number): boolean;
}

function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => {
    s = (Math.imul(s, 1103515245) + 12345) >>> 0;
    return (s >>> 8) / 16777216;
  };
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const round8 = (v: number) => Math.round(v / 8) * 8;

/** Distance from a piece's centre to its edge along (ux, uy): round pieces by their radius. */
function exitAlong(sx: number, sy: number, round: boolean, ux: number, uy: number): number {
  if (round) return Math.min(sx, sy) / 2;
  const ax = Math.abs(ux);
  const ay = Math.abs(uy);
  return Math.min(ax > 1e-6 ? sx / 2 / ax : Infinity, ay > 1e-6 ? sy / 2 / ay : Infinity);
}

/** Scenery models per map, at most. */
const MAX_PROPS = 900;

/** Pieces you don't land on (fields, water, ladders). */
const OFF_PATH = new Set<BoxKind>(['wind', 'gravity', 'water', 'ladder']);

/** Time in the air with gravity scaled by `g` (low gravity fields). */
function airTimeG(vz: number, dh: number, g: number): number {
  const G = GRAVITY * g;
  const disc = vz * vz - 2 * G * dh;
  if (disc < 0) return 0;
  return (vz + Math.sqrt(disc)) / G;
}

const PAD_LOOK: Look = ['neon', '#3affb0'];
const WIND_LOOK: Look = ['wind', '#9ad8ff'];
const GRAVITY_LOOK: Look = ['wind', '#c39bff'];
const WATER_LOOK: Look = ['water', '#2a8fc8'];
const LADDER_LOOK: Look = ['ladder', '#c89a5a'];

/** The generator's cursor and helpers. */
class Builder {
  readonly boxes: LevelBox[] = [];
  readonly path: Vec3Tuple[] = [];
  x = 0;
  y = 0;
  z = 0;
  /** Heading (unit vector). */
  hx = 1;
  hy = 0;
  /** Half-extent of the last piece along the heading. */
  half = 256;
  /** Design speed for the next hop. */
  speed = 250;
  top = 300;
  reach = 0.6;
  size = 128;
  /**
   * Gaps measured along the line of travel (true for the newer maps). The first nine maps space
   * pieces by their furthest corner along the heading, which leaves diagonal gaps a little longer
   * than planned; they keep doing so, so their courses stay the same.
   */
  exact = false;
  /** Exact spacing: size of the last piece and the one being placed next (round: by radius). */
  private curSize: [number, number, boolean] | null = null;
  private nextSize: [number, number, boolean] | null = null;

  constructor(readonly rand: () => number) {}

  /** Exact spacing: the first piece (placed with add(), not advance()) has this size. */
  startFrom(sx: number, sy: number) {
    this.curSize = [sx, sy, false];
  }

  add(
    kind: BoxKind,
    cx: number,
    cy: number,
    top: number,
    sx: number,
    sy: number,
    thick: number,
    look: Look,
    extra: Partial<LevelBox> = {},
  ) {
    const b: LevelBox = {
      min: [Math.round(cx - sx / 2), Math.round(cy - sy / 2), Math.round(top - thick)],
      max: [Math.round(cx + sx / 2), Math.round(cy + sy / 2), Math.round(top)],
      kind,
      material: look[0],
      color: look[1],
      ...extra,
    };
    this.boxes.push(b);
    if (!OFF_PATH.has(kind) && (kind !== 'solid' || extra.shape === 'ramp')) this.path.push([cx, cy, top]);
    return b;
  }

  /** Half-extent of a piece of size (sx, sy) along the heading. */
  extent(sx: number, sy: number, round = false) {
    const ax = Math.abs(this.hx);
    const ay = Math.abs(this.hy);
    if (!this.exact) return (ax * sx + ay * sy) / 2;
    this.nextSize = [sx, sy, round];
    return exitAlong(sx, sy, round, this.hx, this.hy);
  }

  /**
   * Move the cursor to the next piece: an edge gap of `gap` between the pieces along the line to
   * it, `lateral` to the side of the heading.
   */
  advance(gap: number, nextHalf: number, lateral: number) {
    let d = this.half + gap + nextHalf;
    let along = Math.sqrt(Math.max(d * d - lateral * lateral, (d * 0.3) ** 2));
    const a = this.curSize;
    const n = this.nextSize;
    this.curSize = n;
    this.nextSize = null;
    if (this.exact && a && n) {
      // The gap along the line you actually jump (it bends away from the heading by the lateral).
      const len = Math.hypot(along, lateral);
      const ux = (this.hx * along - this.hy * lateral) / len;
      const uy = (this.hy * along + this.hx * lateral) / len;
      d = exitAlong(a[0], a[1], a[2], ux, uy) + gap + exitAlong(n[0], n[1], n[2], ux, uy);
      along = Math.sqrt(Math.max(d * d - lateral * lateral, (d * 0.3) ** 2));
    }
    this.x += this.hx * along - this.hy * lateral;
    this.y += this.hy * along + this.hx * lateral;
    this.half = nextHalf;
  }

  /** The gap for the next hop, `dh` higher, using `frac` of this stage's reach. */
  gap(dh: number, frac = 1) {
    // Less a little: players don't take off and land with the box hanging over both edges.
    return Math.max(56, round8(maxGap(this.speed, dh) * this.reach * frac - 48));
  }

  /** One bhop hop: a block of `size`, `lateral` to the side, `dh` higher. */
  hop(
    look: Look,
    lateral: number,
    dh: number,
    extra: Partial<LevelBox> = {},
    size = this.size,
    thick = 24,
    frac = 1,
  ) {
    const g = this.gap(dh, frac);
    const round = !!extra.shape && extra.shape !== 'box' && extra.shape !== 'ramp';
    this.advance(g, this.extent(size, size, round), lateral);
    this.z += dh;
    const b = this.add('block', this.x, this.y, this.z, size, size, thick, look, extra);
    this.speed = Math.min(this.top, this.speed + 22);
    return b;
  }

  /**
   * A field (wind, gravity) over the stretch from `(x0, y0)` to `(x1, y1)`, `side` units either side
   * of it, from `z0` up to `z1`.
   */
  field(
    kind: 'wind' | 'gravity',
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    side: number,
    z0: number,
    z1: number,
    look: Look,
    extra: Partial<LevelBox>,
  ) {
    const sx = Math.abs(x1 - x0) + 2 * side * Math.abs(this.hy) + 2;
    const sy = Math.abs(y1 - y0) + 2 * side * Math.abs(this.hx) + 2;
    return this.add(kind, (x0 + x1) / 2, (y0 + y1) / 2, z1, sx, sy, z1 - z0, look, extra);
  }

  /** Snap the heading to the nearest axis (for walls and ramps, which are axis-aligned). */
  straighten() {
    if (Math.abs(this.hx) >= Math.abs(this.hy)) [this.hx, this.hy] = [Math.sign(this.hx), 0];
    else [this.hx, this.hy] = [0, Math.sign(this.hy)];
  }

  turn(a: number) {
    const c = Math.cos(a);
    const s = Math.sin(a);
    [this.hx, this.hy] = [this.hx * c - this.hy * s, this.hx * s + this.hy * c];
  }
}

/* ---------------- Modules ---------------- */

/**
 * Seconds for one full back-and-forth of moving blocks, by map difficulty (1 easiest .. 5): slow on
 * easy maps, quicker on hard ones. Sliders and elevators.
 */
const SLIDE_PERIOD = [6, 5, 4, 3.2, 2.6];
const BOB_PERIOD = [5, 4.4, 3.6, 3, 2.6];

function runModule(b: Builder, m: Module, look: Look, solid: Look, n: number, hard: number, difficulty = 3) {
  const d = Math.max(0, Math.min(4, Math.round(difficulty) - 1));
  const r = b.rand;
  const size = b.size;
  switch (m) {
    case 'line':
      for (let i = 0; i < n; i++) b.hop(look, (r() - 0.5) * size * 0.5, 0);
      break;
    case 'zigzag': {
      // Big hops from side to side.
      const amp = size * lerp(0.9, 1.6, hard);
      for (let i = 0; i < n; i++) b.hop(look, (i % 2 ? 1 : -1) * amp, 0);
      break;
    }
    case 'shapes': {
      const shapes: Shape[] = ['tri', 'hex', 'diamond', 'cylinder', 'oct'];
      for (let i = 0; i < n; i++) {
        const shape = shapes[Math.floor(r() * shapes.length)];
        // Points and narrow tops: a bit bigger so they land the same.
        const s = round8(size * (shape === 'tri' || shape === 'diamond' ? 1.3 : 1.1));
        b.hop(look, (i % 2 ? 1 : -1) * size * 0.7, 0, { shape, turn: Math.floor(r() * 4) }, s);
      }
      break;
    }
    case 'curve': {
      // An S: turn one way, then back, so the stage keeps its direction.
      const dir = r() < 0.5 ? 1 : -1;
      const step = lerp(0.22, 0.38, hard);
      for (let i = 0; i < n; i++) {
        b.turn(dir * step * (i < n / 2 ? 1 : -1));
        b.hop(look, 0, 0);
      }
      break;
    }
    case 'stairs':
      for (let i = 0; i < n; i++) b.hop(look, (r() - 0.5) * size * 0.6, i < n - 1 ? 16 : -48);
      break;
    case 'drop':
      for (let i = 0; i < n; i++)
        b.hop(look, (i % 2 ? 1 : -1) * size * 0.6, -(32 + Math.floor(r() * 3) * 16));
      break;
    case 'pillars':
      // Tall round columns, up and down.
      for (let i = 0; i < n; i++)
        b.hop(look, (r() - 0.5) * size, i % 2 ? 24 : -24, { shape: 'cylinder' }, round8(size * 0.95), 520);
      break;
    case 'window': {
      // Hop through holes in walls (walls line up with the axes, so straighten the heading).
      b.straighten();
      for (let i = 0; i < n; i++) {
        const g = b.gap(0);
        const wallAt = b.half + g / 2;
        const wx = b.x + b.hx * wallAt;
        const wy = b.y + b.hy * wallAt;
        const hole = size + 112;
        const thick = 24;
        // The wall spans across the heading: pieces left, right, above and below the hole.
        const px = -b.hy;
        const py = b.hx;
        const wall = (lat: number, w: number, z0: number, z1: number) => {
          const cx = wx + px * lat;
          const cy = wy + py * lat;
          const sx = Math.abs(b.hx) * thick + Math.abs(px) * w;
          const sy = Math.abs(b.hy) * thick + Math.abs(py) * w;
          b.add('solid', cx, cy, z1, sx, sy, z1 - z0, solid);
        };
        const side = 300;
        wall(-(hole / 2 + side / 2), side, b.z - 220, b.z + 420);
        wall(hole / 2 + side / 2, side, b.z - 220, b.z + 420);
        wall(0, hole, b.z + 200, b.z + 420);
        wall(0, hole, b.z - 220, b.z - 80);
        b.hop(look, 0, 0);
      }
      break;
    }
    case 'movers': {
      // Blocks sliding side to side, centred on the line; a little more room for the timing.
      const amp = round8(size * lerp(0.6, 1.1, hard));
      for (let i = 0; i < n; i++) {
        // Slow on easy maps, faster on hard ones, and a little faster toward the end of each map.
        const period = SLIDE_PERIOD[d] * lerp(1, 0.8, hard) + r() * 0.4;
        const px = -b.hy;
        const py = b.hx;
        const blk = b.hop(look, 0, 0, {}, size, 24, 0.8);
        // Start at one end of the travel and slide to the other: the line is the middle.
        const ox = Math.round(px * amp);
        const oy = Math.round(py * amp);
        blk.min = [blk.min[0] - ox, blk.min[1] - oy, blk.min[2]];
        blk.max = [blk.max[0] - ox, blk.max[1] - oy, blk.max[2]];
        blk.move = { offset: [ox * 2, oy * 2, 0], period, phase: r() };
      }
      break;
    }
    case 'elevator':
      // Blocks bobbing down and up again: always reachable, a different landing height each time.
      for (let i = 0; i < n; i++)
        b.hop(
          look,
          (i % 2 ? 1 : -1) * size * 0.5,
          0,
          { move: { offset: [0, 0, -UPDOWN_TRAVEL], period: BOB_PERIOD[d] + r() * 0.6, phase: r() } },
          size,
          24,
          0.9,
        );
      break;
    case 'boost': {
      // A booster pad and a long launch to a wide landing.
      const pad = 192;
      b.advance(b.gap(0, 0.8), b.extent(pad, pad), 0);
      const bh = 300;
      const bz = 170;
      b.add('boost', b.x, b.y, b.z, pad, pad, 32, ['neon', '#ffd23a'], {
        boost: [Math.round(b.hx * bh), Math.round(b.hy * bh), bz],
      });
      const launch = b.speed + bh;
      const land = round8(Math.max(size, 144) * 1.5);
      b.advance(round8(maxGap(launch, 0, JUMP + bz) * b.reach * 0.85), b.extent(land, land), 0);
      b.add('block', b.x, b.y, b.z, land, land, 24, look);
      b.speed = Math.min(b.top + 60, launch * 0.85);
      break;
    }
    case 'ramp': {
      // A ramp down: land on it and slide, then hop off the bottom.
      const w = 256;
      const len = 448;
      const drop = 192;
      // Straighten the heading so the ramp lines up with it.
      b.straighten();
      const axisX = b.hx !== 0;
      b.advance(b.gap(0, 0.8), len / 2, 0);
      // The ramp rises toward where we came from.
      const turn = axisX ? (b.hx > 0 ? 2 : 0) : b.hy > 0 ? 3 : 1;
      b.add('solid', b.x, b.y, b.z, axisX ? len : w, axisX ? w : len, drop, solid, { shape: 'ramp', turn });
      b.z -= drop;
      // A landing pad flush with the bottom: sliding off the end lands on it, then hop on.
      const pad = 256;
      b.advance(0, b.extent(pad, pad), 0);
      b.add('solid', b.x, b.y, b.z, pad, pad, 48, solid);
      b.path.push([b.x, b.y, b.z]);
      b.speed = Math.min(b.top + 40, b.speed + 60);
      // Off the pad you jump from standing plus what the slide gave you.
      if (b.exact) b.speed = Math.min(b.speed, 300);
      break;
    }
    case 'longjump': {
      // Solid platforms: no bhop block rule, but long run-and-jump gaps.
      for (let i = 0; i < n; i++) {
        b.speed = 255;
        const s = 224;
        b.advance(round8(maxGap(255, 0) * lerp(0.62, 0.76, hard)), b.extent(s, s), 0);
        b.add('solid', b.x, b.y, b.z, s, s, 48, solid);
        b.path.push([b.x, b.y, b.z]);
      }
      break;
    }
    case 'pads': {
      // Launch pads (bhop maps' upward trigger_push): land on one and it throws you up to a block
      // higher up. No jump needed. Once thrown you can't cut the flight short, so the landing is where
      // the flight comes down: a big block that catches launches from 250 to 420 u/s.
      const rise = 256;
      const launch = 760;
      const land = 288;
      for (let i = 0; i < n; i++) {
        const pad = round8(Math.max(size, 112) * 1.25);
        b.advance(b.gap(0, 0.9), b.extent(pad, pad), (i % 2 ? 1 : -1) * size * 0.3);
        b.add('pad', b.x, b.y, b.z, pad, pad, 24, PAD_LOOK, { boost: [0, 0, launch] });
        // 250 u/s from the pad's middle comes down about 366 further on; 420 u/s about 614.
        b.advance(round8(340 - pad / 2), b.extent(land, land), 0);
        b.z += rise;
        b.add('block', b.x, b.y, b.z, land, land, 24, look);
      }
      break;
    }
    case 'windgap': {
      // A long gap with wind blowing along it (a sideways trigger_push): jump in, it carries you over.
      const push = 420;
      for (let i = 0; i < n; i++) {
        const land = round8(size * 1.6);
        const fromHalf = b.half;
        // Starts past the edge by more than the player's half width: standing at the edge, you're not in it.
        const x0 = b.x + b.hx * (fromHalf + 24);
        const y0 = b.y + b.hy * (fromHalf + 24);
        b.advance(round8(maxGap(b.speed + push * 0.8, 0) * b.reach), b.extent(land, land), 0);
        const x1 = b.x - b.hx * b.half;
        const y1 = b.y - b.hy * b.half;
        b.field('wind', x0, y0, x1, y1, 160, b.z - 48, b.z + 360, WIND_LOOK, {
          boost: [Math.round(b.hx * push), Math.round(b.hy * push), 0],
        });
        b.add('block', b.x, b.y, b.z, land, land, 24, look);
        b.speed = Math.min(b.top, b.speed + 60);
      }
      break;
    }
    case 'updraft': {
      // Rising air that cancels gravity: you glide upward at your jump speed along it, then drop onto
      // a block higher up.
      for (let i = 0; i < n; i++) {
        const len = 448;
        const rise = 224;
        // You leave the air still rising and come down 300 to 420 units on (more the slower you went
        // through it): a big block from 250 units past its end.
        const land = 288;
        // Past the edge by more than the player's half width (in it at the edge, it would lift you off).
        const x0 = b.x + b.hx * (b.half + 24);
        const y0 = b.y + b.hy * (b.half + 24);
        const x1 = x0 + b.hx * len;
        const y1 = y0 + b.hy * len;
        b.field('wind', x0, y0, x1, y1, 144, b.z - 32, b.z + 640, WIND_LOOK, { boost: [0, 0, 800] });
        b.advance(24 + len + 250, b.extent(land, land), 0);
        b.z += rise;
        b.add('block', b.x, b.y, b.z, land, land, 24, look);
      }
      break;
    }
    case 'lowgrav': {
      // Low gravity (trigger_gravity): floaty jumps over long gaps, from a platform into the field. A
      // floaty jump goes a long way, so each block is big and sits where a jump at the design speed
      // comes down (faster or slower still lands on it; hold back to brake in the air).
      const g = 0.5;
      b.straighten();
      const entry = 288;
      b.advance(b.gap(0, 0.8), b.extent(entry, entry), 0);
      b.add('solid', b.x, b.y, b.z, entry, entry, 48, solid);
      b.path.push([b.x, b.y, b.z]);
      // The field starts past the platform (you take off in normal gravity, then float).
      const x0 = b.x + b.hx * (b.half + 24);
      const y0 = b.y + b.hy * (b.half + 24);
      let top = b.z;
      let bottom = b.z;
      const flight = Math.min(b.speed, 320);
      for (let i = 0; i < n; i++) {
        const dh = i % 2 ? 32 : -24;
        const s2 = round8(Math.max(size * 1.6, 192));
        const gap = Math.max(96, round8(flight * airTimeG(JUMP, dh, g) - s2 / 2));
        b.advance(gap, b.extent(s2, s2), (r() - 0.5) * size * 0.3);
        b.z += dh;
        top = Math.max(top, b.z);
        bottom = Math.min(bottom, b.z);
        b.add('block', b.x, b.y, b.z, s2, s2, 24, look);
      }
      const x1 = b.x + b.hx * b.half;
      const y1 = b.y + b.hy * b.half;
      b.field('gravity', x0, y0, x1, y1, 320, bottom - 400, top + 900, GRAVITY_LOOK, { gravity: g });
      break;
    }
    case 'surf': {
      // Surf ramps: drop onto the side of a steep ramp, hold strafe into it and ride it to the end.
      b.straighten();
      const axisX = b.hx !== 0;
      const w = 448;
      const h = 416;
      let ridge = b.z;
      for (let i = 0; i < n; i++) {
        const len = round8(1100 + r() * 400);
        b.advance(64, len / 2, 0);
        ridge = b.z - 96;
        b.add('solid', b.x, b.y, ridge, axisX ? len : w, axisX ? w : len, h, ['metal', '#7f8fa8'], {
          shape: 'wedge',
          turn: axisX ? 0 : 1,
        });
        b.z = ridge - Math.round(h * 0.35);
      }
      const pad = 352;
      b.advance(96, b.extent(pad, pad), 0);
      // Low enough that riding the slope partway down still lands on it.
      b.z = ridge - Math.round(h * 0.62);
      b.add('solid', b.x, b.y, b.z, pad, pad, 48, solid);
      b.path.push([b.x, b.y, b.z]);
      b.speed = Math.min(b.top + 80, 420);
      break;
    }
    case 'ladder': {
      // A tower with a ladder up its face: jump onto the ladder, climb, carry on from the top.
      b.straighten();
      const axisX = b.hx !== 0;
      const height = 320;
      const across = 288;
      const along = 192;
      b.advance(64, along / 2, 0);
      const base = b.z;
      b.add(
        'solid',
        b.x,
        b.y,
        base + height,
        axisX ? along : across,
        axisX ? across : along,
        height + 96,
        solid,
      );
      // On the near face, from a little below where you arrive to the top.
      const lx = b.x - b.hx * (along / 2 + 8);
      const ly = b.y - b.hy * (along / 2 + 8);
      b.add('ladder', lx, ly, base + height, axisX ? 16 : 112, axisX ? 112 : 16, height + 64, LADDER_LOOK);
      b.z = base + height;
      b.path.push([b.x, b.y, b.z]);
      b.speed = 250;
      break;
    }
    case 'swim': {
      // A pool to swim across, then jump out over its far edge (Source's water jump).
      b.straighten();
      const axisX = b.hx !== 0;
      const len = 640;
      const wide = 384;
      const depth = 320;
      // A little below the ledge you climb out on: floating at the wall, it's under eye height and
      // the water jump pops you up onto it.
      const surface = b.z - 12;
      b.advance(32, len / 2, 0);
      const sx = (along: number, acr: number) => (axisX ? along : acr);
      const sy = (along: number, acr: number) => (axisX ? acr : along);
      b.add('water', b.x, b.y, surface, sx(len, wide), sy(len, wide), depth, WATER_LOOK);
      // Floor and side walls keep you in.
      b.add('solid', b.x, b.y, surface - depth, sx(len, wide + 64), sy(len, wide + 64), 72, solid);
      for (const side of [1, -1]) {
        const ox = -b.hy * side * (wide / 2 + 16);
        const oy = b.hx * side * (wide / 2 + 16);
        b.add(
          'solid',
          b.x + ox,
          b.y + oy,
          b.z + 64,
          sx(len, 32),
          sy(len, 32),
          b.z + 64 - (surface - depth),
          solid,
        );
      }
      // The way out: a ledge at the far end, just above the water.
      const out = 256;
      b.advance(0, out / 2, 0);
      b.add('solid', b.x, b.y, b.z, sx(out, wide + 64), sy(out, wide + 64), depth + 96, solid);
      b.path.push([b.x, b.y, b.z]);
      b.speed = 250;
      break;
    }
    case 'spinners': {
      // Round platforms turning under you (func_rotating): they carry you round while you're on them.
      const rate = [30, 45, 60, 80, 100][d];
      for (let i = 0; i < n; i++)
        b.hop(
          look,
          (i % 2 ? 1 : -1) * size * 0.35,
          0,
          { shape: 'cylinder', spin: (i % 2 ? 1 : -1) * rate },
          round8(size * 1.25),
          24,
          0.7,
        );
      break;
    }
    case 'blinkers': {
      // Blocks that come and go on a timer (func_wall_toggle with a logic_timer).
      for (let i = 0; i < n; i++)
        b.hop(
          look,
          (r() - 0.5) * size * 0.5,
          0,
          {
            blink: {
              on: +(1.8 - d * 0.15).toFixed(2),
              off: +(0.7 + d * 0.05).toFixed(2),
              phase: +((i * 0.37) % 1).toFixed(2),
            },
          },
          round8(size * 1.2),
        );
      break;
    }
  }
}

/** Modules the level builder offers as ready-made obstacles. */
export const PREFABS: { id: Module; label: string; count: number }[] = [
  { id: 'line', label: 'Block line', count: 5 },
  { id: 'zigzag', label: 'Zigzag', count: 6 },
  { id: 'shapes', label: 'Shaped blocks', count: 5 },
  { id: 'curve', label: 'S-curve', count: 6 },
  { id: 'stairs', label: 'Stairs up', count: 5 },
  { id: 'drop', label: 'Drops', count: 4 },
  { id: 'window', label: 'Window walls', count: 3 },
  { id: 'pillars', label: 'Pillars', count: 5 },
  { id: 'movers', label: 'Sliding blocks', count: 4 },
  { id: 'elevator', label: 'Bobbing blocks', count: 4 },
  { id: 'boost', label: 'Booster launch', count: 1 },
  { id: 'ramp', label: 'Ramp + pad', count: 1 },
  { id: 'longjump', label: 'Long jumps', count: 2 },
  { id: 'pads', label: 'Launch pads', count: 2 },
  { id: 'windgap', label: 'Wind gap', count: 1 },
  { id: 'updraft', label: 'Updraft', count: 1 },
  { id: 'lowgrav', label: 'Low gravity jumps', count: 3 },
  { id: 'surf', label: 'Surf ramps', count: 2 },
  { id: 'ladder', label: 'Ladder tower', count: 1 },
  { id: 'swim', label: 'Swim across', count: 1 },
  { id: 'spinners', label: 'Spinning platforms', count: 4 },
  { id: 'blinkers', label: 'Blinking blocks', count: 4 },
];

/**
 * A ready-made obstacle for the level builder: the module's pieces, starting at `at` (a point on a
 * surface) and heading along `yaw`. Gaps assume a player arriving at about 300 u/s.
 */
export function prefab(
  m: Module,
  at: Vec3Tuple,
  yaw: number,
  size: number,
  look: Look,
  solid: Look,
  seed = 1,
): LevelBox[] {
  const b = new Builder(rng(seed));
  b.x = at[0];
  b.y = at[1];
  b.z = at[2];
  b.half = 0;
  b.hx = Math.cos(yaw);
  b.hy = Math.sin(yaw);
  b.size = size;
  b.reach = 0.62;
  b.top = 340;
  b.speed = 300;
  const n = PREFABS.find((p) => p.id === m)?.count ?? 4;
  runModule(b, m, look, solid, n, 0.4);
  return b.boxes;
}

/** How many times the newer modules repeat in a map (fewest, most). */
const NEW_COUNTS: Partial<Record<Module, [number, number]>> = {
  pads: [2, 3],
  windgap: [1, 2],
  updraft: [1, 1],
  lowgrav: [3, 4],
  surf: [1, 2],
  ladder: [1, 1],
  swim: [1, 1],
  spinners: [3, 5],
  blinkers: [4, 6],
};

export function generateLevel(rc: Recipe): LevelDef {
  const rand = rng(rc.seed);
  const b = new Builder(rand);
  b.exact = !!rc.exact;
  b.add('start', 0, 0, 0, 512, 512, 64, rc.start);
  if (b.exact) b.startFrom(512, 512);
  let turnSign = 1;

  for (let s = 0; s < rc.stages; s++) {
    const t = rc.stages > 1 ? s / (rc.stages - 1) : 0;
    const look = rc.blocks[s % rc.blocks.length];
    b.size = round8(lerp(rc.size[0], rc.size[1], t));
    b.reach = lerp(rc.reach[0], rc.reach[1], t);
    b.top = lerp(rc.topSpeed[0], rc.topSpeed[1], t);
    b.speed = 250;
    const mainX = b.hx;
    const mainY = b.hy;
    // Two or three modules per stage, picked from what's unlocked so far (more kinds later on).
    const unlocked = Math.max(
      2,
      Math.min(rc.modules.length, 2 + Math.floor(t * (rc.modules.length - 1) + 0.5)),
    );
    const count = s === 0 ? 2 : 2 + (rand() < 0.4 + t * 0.3 ? 1 : 0);
    let last: Module | null = null;
    for (let k = 0; k < count; k++) {
      let m = rc.modules[Math.floor(rand() * unlocked)];
      if (m === last) m = rc.modules[(rc.modules.indexOf(m) + 1) % unlocked];
      last = m;
      const n = NEW_COUNTS[m]
        ? NEW_COUNTS[m]![0] + Math.floor(rand() * (NEW_COUNTS[m]![1] - NEW_COUNTS[m]![0] + 1))
        : m === 'boost' || m === 'ramp'
          ? 1
          : m === 'longjump'
            ? 2
            : m === 'window'
              ? 3
              : 4 + Math.floor(rand() * 3);
      runModule(b, m, look, rc.solid, n, t, rc.difficulty);
    }
    // Back to the stage's main heading for the platform.
    b.hx = mainX;
    b.hy = mainY;
    const isLast = s === rc.stages - 1;
    const plat = isLast ? 640 : 320;
    const platHalf = b.extent(plat, plat);
    b.advance(Math.max(64, round8(maxGap(b.speed, 0) * b.reach * 0.8)), platHalf, 0);
    b.add(isLast ? 'end' : 'checkpoint', b.x, b.y, b.z, plat, plat, 64, isLast ? rc.end : rc.checkpoint);
    if (isLast) continue;
    if (rc.layout === 'arc') {
      // Long sweeping curves: the same way for four stages, then back.
      b.turn((Math.floor(s / 4) % 2 ? -1 : 1) * 0.5);
    } else if (rc.layout === 'zig') {
      // A wide zigzag: a sharp turn one way, then the other, at every checkpoint.
      b.turn((s % 2 ? 1 : -1) * 0.8);
    } else if (s % 2 === 1) {
      // Turn 90 degrees at every other checkpoint so the map snakes instead of running off forever.
      [b.hx, b.hy] = [-b.hy * turnSign, b.hx * turnSign];
      turnSign = -turnSign;
    }
  }

  // Scenery, kept clear of the course.
  const path = b.path;
  const minZ = Math.min(...b.boxes.map((x) => x.min[2]));
  const bmin: Vec3Tuple = [Infinity, Infinity, minZ];
  const bmax: Vec3Tuple = [-Infinity, -Infinity, Math.max(...path.map((p) => p[2]))];
  for (const p of path) {
    bmin[0] = Math.min(bmin[0], p[0]);
    bmin[1] = Math.min(bmin[1], p[1]);
    bmax[0] = Math.max(bmax[0], p[0]);
    bmax[1] = Math.max(bmax[1], p[1]);
  }
  const scenery: Scenery = {
    path,
    platforms: b.boxes
      .filter((x) => x.kind === 'start' || x.kind === 'checkpoint' || x.kind === 'end')
      .map((x) => [(x.min[0] + x.max[0]) / 2, (x.min[1] + x.max[1]) / 2, x.max[2]] as Vec3Tuple),
    rand,
    bounds: { min: bmin, max: bmax },
    add: (min, max, material, color, shape) =>
      b.boxes.push({
        min: min.map(Math.round) as Vec3Tuple,
        max: max.map(Math.round) as Vec3Tuple,
        kind: 'deco',
        material,
        color,
        ...(shape ? { shape } : {}),
      }),
    clearOf: (px, py, clear) => path.every((p) => Math.hypot(p[0] - px, p[1] - py) > clear),
  };
  rc.scenery(scenery);
  const props = rc.decorate ? decorate(rc, path, b.boxes, bmin, bmax, minZ) : [];

  return {
    v: 1,
    name: rc.name,
    title: rc.title,
    author: 'CSS-BHOP',
    difficulty: rc.difficulty,
    theme: rc.theme,
    boxes: b.boxes,
    spawn: { pos: [-150, 0, DIST_EPSILON], yaw: 0 },
    ...(props.length ? { props } : {}),
    failZ: minZ - 400,
  };
}

/**
 * Scenery models for a map, standing on the floor far below (where the void floor is drawn) or
 * wherever the recipe puts them; anything near the course stays well below it, so nothing blocks
 * the way or the view. Their own random stream: the course is the same with or without them.
 */
function decorate(
  rc: Recipe,
  path: Vec3Tuple[],
  boxes: LevelBox[],
  bmin: Vec3Tuple,
  bmax: Vec3Tuple,
  minZ: number,
): LevelProp[] {
  // Pieces you can touch: models near one stay below its top.
  const solids = boxes.filter((x) => !OFF_PATH.has(x.kind) && x.kind !== 'deco');
  const props: LevelProp[] = [];
  const rand = rng(rc.seed * 7919 + 17);
  // Segments of the course in the air (each from one piece you stand on to the next).
  const near = (x: number, y: number, r: number, top: number) => {
    for (let i = 0; i + 1 < path.length; i++) {
      const [ax, ay, az] = path[i];
      const [bx, by, bz] = path[i + 1];
      const dx = bx - ax;
      const dy = by - ay;
      const l2 = dx * dx + dy * dy;
      const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2)) : 0;
      const d = Math.hypot(ax + dx * t - x, ay + dy * t - y);
      if (d < r + 360 && top > Math.min(az, bz) - 160) return true;
    }
    for (const s of solids) {
      const dx = Math.max(s.min[0] - x, 0, x - s.max[0]);
      const dy = Math.max(s.min[1] - y, 0, y - s.max[1]);
      if (dx * dx + dy * dy < (r + 200) ** 2 && top > s.max[2] - 120) return true;
    }
    return false;
  };
  const push = (model: string, x: number, y: number, z: number, scale: number, yaw: number) => {
    const s = PROP_SIZES[model];
    props.push({
      model,
      pos: [Math.round(x), Math.round(y), Math.round(z - s[3] * scale)],
      yaw: +yaw.toFixed(3),
      scale: +scale.toFixed(2),
    });
  };
  const platforms = boxes
    .filter((x) => x.kind === 'start' || x.kind === 'checkpoint' || x.kind === 'end')
    .map((x) => ({
      x: (x.min[0] + x.max[0]) / 2,
      y: (x.min[1] + x.max[1]) / 2,
      z: x.max[2],
      sx: x.max[0] - x.min[0],
      sy: x.max[1] - x.min[1],
    }));
  // Other pieces you stand on: models on a platform keep away from the ones next to it.
  const others = solids.filter((x) => x.kind !== 'start' && x.kind !== 'checkpoint' && x.kind !== 'end');
  const islands: { x: number; y: number; r: number }[] = [];
  // Where the floor is drawn (BoxLevelView: 400 under the fail height or the lowest piece, scenery
  // included); islands never reach below that lowest piece, so it stays put.
  let lowest = minZ - 400;
  for (const x of boxes) lowest = Math.min(lowest, x.min[2]);
  const floor = lowest - 400;
  const decor: Decor = {
    rand,
    path,
    bounds: { min: bmin, max: bmax },
    floor,
    platforms,
    prop: (model, x, y, z, scale, yaw = 0) => {
      const s = PROP_SIZES[model];
      if (!s || props.length >= MAX_PROPS) return false;
      const r = Math.max(s[0], s[2]) * scale * 0.5;
      const top = z + s[1] * scale;
      if (near(x, y, r, top)) return false;
      push(model, x, y, z, scale, yaw);
      return true;
    },
    platformProp: (model, x, y, z, scale, yaw = 0) => {
      const s = PROP_SIZES[model];
      if (!s || props.length >= MAX_PROPS) return false;
      // Small (below eye height), on the platform, and not next to a piece you jump to or from.
      const r = Math.max(s[0], s[2]) * scale * 0.5;
      if (s[1] * scale > 130 || r > 56) return false;
      const on = platforms.find(
        (p) =>
          Math.abs(x - p.x) <= p.sx / 2 - r && Math.abs(y - p.y) <= p.sy / 2 - r && Math.abs(z - p.z) < 1,
      );
      if (!on) return false;
      for (const o of others) {
        const dx = Math.max(o.min[0] - x, 0, x - o.max[0]);
        const dy = Math.max(o.min[1] - y, 0, y - o.max[1]);
        if (dx * dx + dy * dy < (r + 140) ** 2) return false;
      }
      push(model, x, y, z, scale, yaw);
      return true;
    },
    island: (x, y, top, r, thick, look, shape = 'oct') => {
      // Beside the course, never on the way: clear of its line and of every piece.
      if (lineDistance(path, x, y) < r + 520) return false;
      // (Its whole square footprint, corners included.)
      for (const o of solids) {
        const dx = Math.max(o.min[0] - (x + r), 0, x - r - o.max[0]);
        const dy = Math.max(o.min[1] - (y + r), 0, y - r - o.max[1]);
        if (dx * dx + dy * dy < 320 ** 2) return false;
      }
      if (islands.some((o) => Math.hypot(o.x - x, o.y - y) < o.r + r + 160)) return false;
      if (top - thick * 2.2 < lowest) return false;
      islands.push({ x, y, r });
      const box = (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, mat: Look) =>
        boxes.push({
          min: [Math.round(x0), Math.round(y0), Math.round(z0)],
          max: [Math.round(x1), Math.round(y1), Math.round(z1)],
          kind: 'deco',
          material: mat[0],
          color: mat[1],
          shape,
        });
      // The top, and a narrower rocky underside so it looks like it floats.
      box(x - r, y - r, top - thick, x + r, y + r, top, look);
      box(x - r * 0.6, y - r * 0.6, top - thick * 2.2, x + r * 0.6, y + r * 0.6, top - thick, [
        'stone',
        '#6f6a62',
      ]);
      return true;
    },
  };
  rc.decorate!(decor);
  return props;
}

/** Distance from (x, y) to the course's line (each piece you stand on to the next), on the ground plan. */
function lineDistance(path: Vec3Tuple[], x: number, y: number): number {
  let best = Infinity;
  for (let i = 0; i + 1 < path.length; i++) {
    const [ax, ay] = path[i];
    const [bx, by] = path[i + 1];
    const dx = bx - ax;
    const dy = by - ay;
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2)) : 0;
    best = Math.min(best, Math.hypot(ax + dx * t - x, ay + dy * t - y));
  }
  return best;
}

/* ---------------- The maps ---------------- */

const ALL: Module[] = [
  'line',
  'zigzag',
  'shapes',
  'curve',
  'stairs',
  'window',
  'drop',
  'movers',
  'pillars',
  'elevator',
  'boost',
  'ramp',
  'longjump',
];

const RECIPES: Recipe[] = [
  {
    name: 'bhop_meadow',
    title: 'Meadow',
    difficulty: 1,
    stages: 10,
    seed: 11,
    theme: T({}),
    start: ['planks', '#b98a54'],
    checkpoint: ['planks', '#a7794a'],
    end: ['planks', '#c99a5e'],
    blocks: [
      ['grass', '#5fae3e'],
      ['planks', '#c58c4e'],
      ['grass', '#78c24a'],
      ['tiles', '#e2d3a6'],
    ],
    solid: ['stone', '#9a9a8a'],
    size: [136, 120],
    reach: [0.5, 0.6],
    topSpeed: [300, 330],
    modules: ['line', 'zigzag', 'curve', 'shapes', 'stairs', 'window', 'ramp'],
    scenery: (s) => {
      trees(s, 70, '#7a5230', '#3f8f2e');
      columns(s, 'planks', '#8a6238', 900, 'cylinder');
    },
  },
  {
    name: 'bhop_toybox',
    title: 'Toybox',
    difficulty: 1,
    stages: 12,
    seed: 17,
    theme: T({ skyTop: '#ff9ad5', skyHorizon: '#fff1c9', fog: '#ffe6f2', voidColor: '#8fd4ff' }),
    start: ['tiles', '#ffffff'],
    checkpoint: ['tiles', '#ffe36e'],
    end: ['tiles', '#ff7aa8'],
    blocks: [
      ['tiles', '#ff6f91'],
      ['tiles', '#59c3ff'],
      ['tiles', '#7ce06b'],
      ['tiles', '#ffb84d'],
      ['tiles', '#b18cff'],
    ],
    solid: ['tiles', '#f4f4f4'],
    size: [128, 112],
    reach: [0.54, 0.64],
    topSpeed: [310, 340],
    modules: ['zigzag', 'shapes', 'line', 'movers', 'curve', 'window', 'elevator', 'boost'],
    scenery: (s) => {
      // Giant toy blocks and balls.
      const colors = ['#ff6f91', '#59c3ff', '#7ce06b', '#ffb84d', '#b18cff', '#ffe36e'];
      scatter(s, 70, 700, (x, y) => {
        const w = 120 + s.rand() * 260;
        const z = floorZ(s, 300 + s.rand() * 900);
        const shape = (['box', 'cylinder', 'hex', 'tri'] as Shape[])[Math.floor(s.rand() * 4)];
        s.add(
          [x - w, y - w, z - w * 2],
          [x + w, y + w, z],
          'tiles',
          colors[Math.floor(s.rand() * colors.length)],
          shape,
        );
      });
      columns(s, 'tiles', '#ffffff', 900, 'cylinder');
    },
  },
  {
    name: 'bhop_canyon',
    title: 'Sunset Canyon',
    difficulty: 2,
    stages: 14,
    seed: 23,
    theme: T({
      skyTop: '#6b3b7a',
      skyHorizon: '#ffb26b',
      fog: '#f0a070',
      fogNear: 2500,
      fogFar: 14000,
      voidColor: '#a65a33',
    }),
    start: ['sand', '#d9b27a'],
    checkpoint: ['stone', '#b98a63'],
    end: ['sand', '#e7c48c'],
    blocks: [
      ['stone', '#c27a4a'],
      ['sand', '#d8a768'],
      ['bricks', '#b4643a'],
      ['tiles', '#d39a5a'],
    ],
    solid: ['stone', '#9e6b4a'],
    size: [120, 104],
    reach: [0.56, 0.68],
    topSpeed: [320, 360],
    modules: [
      'line',
      'stairs',
      'zigzag',
      'drop',
      'elevator',
      'ramp',
      'window',
      'pillars',
      'movers',
      'curve',
      'longjump',
    ],
    scenery: (s) => {
      scatter(s, 40, 900, (x, y) => {
        let w = 300 + s.rand() * 500;
        let z = floorZ(s, 1100);
        for (let k = 0; k < 3; k++) {
          const h = Math.min(300 + s.rand() * 400, floorZ(s, 250) - z);
          if (h < 50) break;
          s.add(
            [x - w, y - w * 0.8, z],
            [x + w, y + w * 0.8, z + h],
            k % 2 ? 'stone' : 'sand',
            k % 2 ? '#a8603a' : '#d29a5c',
            'oct',
          );
          z += h;
          w *= 0.7;
        }
      });
      columns(s, 'stone', '#9a5e3c', 1100);
    },
  },
  {
    name: 'bhop_ocean',
    title: 'Ocean Ruins',
    difficulty: 2,
    stages: 16,
    seed: 29,
    theme: T({
      skyTop: '#1d5fa8',
      skyHorizon: '#a8e6ff',
      fog: '#8fd0f0',
      fogNear: 2500,
      fogFar: 14000,
      voidColor: '#1c6e9e',
    }),
    start: ['stone', '#c9c2a8'],
    checkpoint: ['tiles', '#e8e2cc'],
    end: ['crystal', '#7fe0ff'],
    blocks: [
      ['stone', '#b8b09a'],
      ['tiles', '#7fc8d8'],
      ['crystal', '#5ad1c8'],
      ['bricks', '#c4b896'],
    ],
    solid: ['stone', '#a39c86'],
    size: [120, 100],
    reach: [0.58, 0.7],
    topSpeed: [330, 370],
    modules: [
      'zigzag',
      'line',
      'pillars',
      'shapes',
      'window',
      'movers',
      'drop',
      'curve',
      'elevator',
      'boost',
    ],
    scenery: (s) => {
      pools(s, 18, 'glass', '#2a8fc8', 900);
      // Broken columns poking out of the sea.
      towers(
        s,
        60,
        [
          ['stone', '#d8d0b8'],
          ['bricks', '#c4b896'],
        ],
        0,
        'cylinder',
      );
      columns(s, 'stone', '#b8b09a', 1000, 'cylinder');
    },
  },
  {
    name: 'bhop_neon',
    title: 'Neon City',
    difficulty: 3,
    stages: 18,
    seed: 37,
    theme: T({
      skyTop: '#05030f',
      skyHorizon: '#2a0f45',
      fog: '#1a0b2e',
      fogNear: 2000,
      fogFar: 13000,
      voidColor: '#0b0614',
    }),
    start: ['metal', '#5a6273'],
    checkpoint: ['neon', '#21e1ff'],
    end: ['neon', '#ff3fb4'],
    blocks: [
      ['neon', '#ff3fb4'],
      ['neon', '#21e1ff'],
      ['metal', '#8a95ae'],
      ['neon', '#9b5cff'],
      ['glass', '#6fd6ff'],
    ],
    solid: ['metal', '#3a4256'],
    size: [104, 88],
    reach: [0.62, 0.74],
    topSpeed: [340, 400],
    modules: [
      'zigzag',
      'line',
      'movers',
      'window',
      'boost',
      'shapes',
      'curve',
      'drop',
      'elevator',
      'pillars',
    ],
    scenery: (s) => {
      scatter(s, 90, 600, (x, y) => {
        const base = floorZ(s, 2500);
        const w = 150 + s.rand() * 250;
        const top = floorZ(s, 300 + s.rand() * 1200);
        s.add([x - w, y - w, base], [x + w, y + w, top], 'metal', s.rand() > 0.5 ? '#2a3040' : '#363a4c');
        s.add(
          [x - w - 8, y - w - 8, top],
          [x + w + 8, y + w + 8, top + 24],
          'neon',
          ['#ff3fb4', '#21e1ff', '#9b5cff'][Math.floor(s.rand() * 3)],
        );
      });
    },
  },
  {
    name: 'bhop_jungle',
    title: 'Jungle Temple',
    difficulty: 3,
    stages: 20,
    seed: 43,
    theme: T({
      skyTop: '#2f6b3a',
      skyHorizon: '#d6f0b0',
      fog: '#b9d99a',
      fogNear: 2200,
      fogFar: 12000,
      voidColor: '#1f4a24',
    }),
    start: ['stone', '#8a8f6a'],
    checkpoint: ['bricks', '#7a8058'],
    end: ['crystal', '#e8d36a'],
    blocks: [
      ['stone', '#7f8a5a'],
      ['grass', '#4f9a35'],
      ['bricks', '#8f8a62'],
      ['planks', '#9a6f40'],
    ],
    solid: ['bricks', '#6f7350'],
    size: [104, 88],
    reach: [0.64, 0.76],
    topSpeed: [350, 410],
    modules: [
      'line',
      'stairs',
      'pillars',
      'window',
      'zigzag',
      'ramp',
      'movers',
      'shapes',
      'drop',
      'longjump',
      'elevator',
    ],
    scenery: (s) => {
      trees(s, 90, '#5a3d22', '#2f7a2a');
      towers(
        s,
        30,
        [
          ['bricks', '#7a7c58'],
          ['stone', '#6a6e4c'],
        ],
        0,
        'box',
      );
      columns(s, 'stone', '#6f7350', 1100);
    },
  },
  {
    name: 'bhop_frost',
    title: 'Frost Peak',
    difficulty: 4,
    stages: 22,
    seed: 41,
    theme: T({
      skyTop: '#9fc4e8',
      skyHorizon: '#eef6ff',
      fog: '#e4efff',
      fogNear: 1800,
      fogFar: 11000,
      ambient: 0.65,
      voidColor: '#dbe8f5',
    }),
    start: ['tiles', '#d8e6f5'],
    checkpoint: ['ice', '#a9d6ff'],
    end: ['crystal', '#bfe4ff'],
    blocks: [
      ['ice', '#9fd0ff'],
      ['crystal', '#7fb8ff'],
      ['ice', '#c2e4ff'],
      ['stone', '#8fa3b8'],
      ['crystal', '#a5a0ff'],
    ],
    solid: ['ice', '#d6ecff'],
    size: [96, 80],
    reach: [0.66, 0.79],
    topSpeed: [360, 430],
    modules: [
      'curve',
      'stairs',
      'zigzag',
      'shapes',
      'drop',
      'pillars',
      'boost',
      'movers',
      'window',
      'ramp',
      'elevator',
      'longjump',
    ],
    scenery: (s) => {
      scatter(s, 110, 600, (x, y) => {
        const h = 600 + s.rand() * 1500;
        let w = 80 + s.rand() * 140;
        let z = floorZ(s, 1200);
        const top = floorZ(s, 250);
        for (let k = 0; k < 4 && z < top; k++) {
          const hh = Math.min(h / 4, top - z);
          if (hh < 16) break;
          s.add(
            [x - w, y - w, z],
            [x + w, y + w, z + hh],
            k % 2 ? 'crystal' : 'ice',
            k % 2 ? '#9cc8ff' : '#d6ecff',
            'hex',
          );
          z += hh;
          w *= 0.68;
        }
      });
      columns(s, 'ice', '#b5dcff', 1300, 'hex');
    },
  },
  {
    name: 'bhop_station',
    title: 'Space Station',
    difficulty: 4,
    stages: 24,
    seed: 47,
    theme: T({
      skyTop: '#000004',
      skyHorizon: '#0c1a3a',
      fog: '#060c1c',
      fogNear: 2500,
      fogFar: 14000,
      ambient: 0.5,
      voidColor: '#04060c',
    }),
    start: ['metal', '#7a8494'],
    checkpoint: ['metal', '#5f6c82'],
    end: ['neon', '#3affb0'],
    blocks: [
      ['metal', '#9aa6ba'],
      ['neon', '#3affb0'],
      ['metal', '#c0c8d6'],
      ['neon', '#ffb03a'],
      ['grid', '#4a5468'],
    ],
    solid: ['metal', '#2e3644'],
    size: [96, 76],
    reach: [0.68, 0.81],
    topSpeed: [370, 440],
    modules: [
      'movers',
      'zigzag',
      'window',
      'boost',
      'elevator',
      'shapes',
      'curve',
      'line',
      'drop',
      'pillars',
      'longjump',
    ],
    scenery: (s) => {
      // Planets far below and drifting hull towers.
      scatter(s, 8, 3000, (x, y) => {
        const r = 900 + s.rand() * 1600;
        const z = floorZ(s, 2500 + s.rand() * 2000);
        s.add(
          [x - r, y - r, z - r],
          [x + r, y + r, z + r],
          'crystal',
          ['#5a3aa8', '#a85a3a', '#3a7aa8'][Math.floor(s.rand() * 3)],
          'oct',
        );
      });
      towers(
        s,
        50,
        [
          ['metal', '#2a3240'],
          ['grid', '#3a4458'],
        ],
        200,
        'hex',
      );
      columns(s, 'metal', '#3a4456', 900);
    },
  },
  {
    name: 'bhop_inferno',
    title: 'Inferno',
    difficulty: 5,
    stages: 26,
    seed: 53,
    theme: T({
      skyTop: '#140202',
      skyHorizon: '#7a1a08',
      fog: '#3a0a04',
      fogNear: 1600,
      fogFar: 10000,
      ambient: 0.5,
      voidColor: '#ff5a10',
    }),
    start: ['bricks', '#6a3a30'],
    checkpoint: ['metal', '#5a4a48'],
    end: ['lava', '#ff6a1a'],
    blocks: [
      ['bricks', '#d0603a'],
      ['stone', '#a08478'],
      ['lava', '#ff7a2a'],
      ['metal', '#c09a88'],
      ['bricks', '#e07a48'],
    ],
    solid: ['stone', '#3a2a26'],
    size: [88, 68],
    reach: [0.7, 0.83],
    topSpeed: [380, 460],
    modules: ALL,
    scenery: (s) => {
      pools(s, 26, 'lava', '#ff6a1a', 700);
      towers(s, 70, [['stone', '#2a1e1c']], 0, 'hex');
      columns(s, 'bricks', '#5a2a20', 1200);
    },
  },
];

/** The built-in maps, in order. Generated on demand and cached. */
// The first nine maps get scenery models too (their courses stay the same).
for (const r of RECIPES) r.decorate ??= OLD_DECOR[r.name];

const ALL_RECIPES = [...RECIPES, ...NEW_RECIPES].sort(
  (a, b) => a.difficulty - b.difficulty || a.stages - b.stages,
);

const cache = new Map<string, LevelDef>();
export const BUILTIN_MAPS = ALL_RECIPES.map((r) => ({
  name: r.name,
  title: r.title,
  difficulty: r.difficulty,
  stages: r.stages,
}));

export function builtinLevel(name: string): LevelDef | null {
  const r = ALL_RECIPES.find((x) => x.name === name);
  if (!r) return null;
  let d = cache.get(name);
  if (!d) cache.set(name, (d = generateLevel(r)));
  return d;
}
